import posthog from "posthog-js";
import { POSTHOG_EVENTS } from "../constants/posthog-events";

/**
 * Safely capture an error event in PostHog
 * @param {Object} ph - PostHog instance (optional)
 * @param {string} eventName - Human-readable event name
 * @param {Error} error - The error object
 * @param {Object} additionalProps - Additional properties to track
 */
export const captureError = (ph, eventName, error, additionalProps = {}) => {
  // If first arg is string, shift arguments
  if (typeof ph === 'string') {
    additionalProps = error || {};
    error = eventName;
    eventName = ph;
    ph = posthog;
  }
  const instance = ph || posthog;
  if (!instance || typeof instance.capture !== 'function') return;

  try {
    instance.capture(eventName, {
      error_message: error?.message || String(error),
      error_stack: error?.stack,
      error_name: error?.name,
      ...additionalProps,
    });
  } catch (e) {
    // Silently fail - don't let PostHog errors break the app
    console.warn('PostHog capture failed:', e);
  }
};

/**
 * Capture API-specific errors with endpoint and response details
 * @param {Object} posthog - PostHog instance
 * @param {string} eventName - Human-readable event name
 * @param {string} endpoint - API endpoint that failed
 * @param {Error} error - The error object
 * @param {Response} response - Optional fetch response object
 */
export const captureApiError = (ph, eventName, endpoint, error, response = null) => {
  // If first arg is string, shift arguments
  if (typeof ph === 'string') {
    response = error;
    error = endpoint;
    endpoint = eventName;
    eventName = ph;
    ph = posthog;
  }
  const instance = ph || posthog;
  if (!instance || typeof instance.capture !== 'function') return;

  try {
    const props = {
      endpoint,
      error_message: error?.message || String(error),
      error_stack: error?.stack,
    };

    if (response) {
      props.status_code = response.status;
      props.status_text = response.statusText;
    }

    instance.capture(eventName, props);
  } catch (e) {
    console.warn('PostHog API error capture failed:', e);
  }
};

/**
 * Safely capture a success event
 * @param {Object} posthog - PostHog instance
 * @param {string} eventName - Human-readable event name
 * @param {Object} properties - Event properties
 */
export const captureEvent = (ph, eventName, properties = {}) => {
  // If first arg is string, shift arguments
  if (typeof ph === 'string') {
    properties = eventName || {};
    eventName = ph;
    ph = posthog;
  }
  const instance = ph || posthog;
  if (!instance || typeof instance.capture !== 'function') return;

  try {
    instance.capture(eventName, properties);
  } catch (e) {
    console.warn('PostHog event capture failed:', e);
  }
};

/**
 * Capture React error boundary errors
 * @param {Object} posthog - PostHog instance
 * @param {Error} error - The error object
 * @param {Object} errorInfo - React error info with component stack
 */
export const captureReactError = (ph, error, errorInfo) => {
  const instance = ph?.capture ? ph : posthog;
  if (!instance || typeof instance.capture !== 'function') return;

  try {
    instance.capture(POSTHOG_EVENTS.REACT_ERROR, {
      error_message: error?.message || String(error),
      error_stack: error?.stack,
      error_name: error?.name,
      component_stack: errorInfo?.componentStack,
    });
  } catch (e) {
    console.warn('PostHog React error capture failed:', e);
  }
};
